/**
 * Basic inventory (assistant role test 2026-10-20, fix 7; module id
 * `inventory`, routes under `/v1/inventory`). Items per office with a par
 * level and the count on hand; an item at or under its par is flagged to
 * reorder. Every count change is kept as a history row and audited.
 */
import { z } from 'zod';

const Id = z.uuid();
const Instant = z.string().min(1);

export const INVENTORY_PERMISSIONS = {
  /** See items and counts. */
  view: 'task.view',
  /** Add items, change par levels and counts. */
  edit: 'task.create',
  /** Archive items. */
  manage: 'settings.manage',
} as const;

export const INVENTORY_ENTITY = 'inventory_item';

export const InventoryItemSchema = z.object({
  id: Id,
  locationId: Id,
  name: z.string(),
  category: z.string().nullable(),
  /** "box of 100", "each". */
  unit: z.string().nullable(),
  parLevel: z.number().int(),
  onHand: z.number().int(),
  /** On hand is at or under the par level. */
  reorder: z.boolean(),
  supplier: z.string().nullable(),
  sku: z.string().nullable(),
  notes: z.string().nullable(),
  updatedAt: Instant,
  countedByName: z.string().nullable(),
  countedAt: Instant.nullable(),
  /** Sold to patients at checkout (W15): toothbrushes, whitening strips. */
  retail: z.boolean(),
  /** The price a patient pays for one (retail items). */
  retailPriceCents: z.number().int().nullable(),
  /** The procedure code the sale posts under in the PMS (the office's retail code); null = none yet. */
  pmsCode: z.string().nullable(),
});
export type InventoryItem = z.infer<typeof InventoryItemSchema>;

export const InventoryListSchema = z.object({
  items: z.array(InventoryItemSchema),
  canEdit: z.boolean(),
  canManage: z.boolean(),
  /**
   * The office's retail code (QA r57): D9999 when it is in the code list, else
   * a code described as retail; null when the office has none (products then
   * sell without a code and the PMS task carries them).
   */
  retailCode: z.string().nullable().optional(),
});
export type InventoryList = z.infer<typeof InventoryListSchema>;

export const ItemBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  category: z.string().trim().max(60).nullable().optional(),
  unit: z.string().trim().max(40).nullable().optional(),
  parLevel: z.number().int().min(0).max(100_000),
  onHand: z.number().int().min(0).max(1_000_000).optional(),
  supplier: z.string().trim().max(120).nullable().optional(),
  sku: z.string().trim().max(60).nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
  retail: z.boolean().optional(),
  retailPriceCents: z.number().int().min(0).max(500_000).nullable().optional(),
  pmsCode: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[A-Za-z0-9.-]+$/, 'Letters, digits, dot and dash only')
    .nullable()
    .optional(),
});
export type ItemBody = z.input<typeof ItemBodySchema>;

export const CountBodySchema = z
  .object({
    /** The new count on hand, or a change (+5 received, -2 used). */
    onHand: z.number().int().min(0).max(1_000_000).optional(),
    delta: z.number().int().min(-1_000_000).max(1_000_000).optional(),
    note: z.string().trim().max(200).nullable().optional(),
  })
  .refine((b) => (b.onHand === undefined) !== (b.delta === undefined), {
    message: 'Send the new count or the change, not both.',
  });
export type CountBody = z.input<typeof CountBodySchema>;

/** A retail sale made at checkout (W15). Patient names are not included. */
export const RetailSaleSchema = z.object({
  id: Id,
  locationId: Id,
  itemId: Id,
  itemName: z.string(),
  pmsCode: z.string(),
  priceCents: z.number().int(),
  patientId: Id,
  appointmentId: Id.nullable(),
  procedureId: Id,
  taskId: Id.nullable(),
  soldByName: z.string(),
  soldAt: Instant,
  voidedAt: Instant.nullable(),
  voidedByName: z.string().nullable(),
});
export type RetailSale = z.infer<typeof RetailSaleSchema>;

export const RetailSalesQuerySchema = z.object({
  /** Office-local dates, inclusive. Default: the last 30 days. */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});

/** `GET /v1/inventory/sales`: retail sold at this office, with totals per product. */
export const RetailSalesSchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  sales: z.array(RetailSaleSchema),
  totals: z.array(
    z.object({
      itemId: Id,
      itemName: z.string(),
      sold: z.number().int(),
      revenueCents: z.number().int(),
    }),
  ),
  /** False when the caller may not see money: prices and revenue are 0. */
  moneyVisible: z.boolean(),
});
export type RetailSales = z.infer<typeof RetailSalesSchema>;
